import isEqual from 'lodash.isequal';
import { reaction, runInAction, toJS } from 'mobx';
import { useEffect, useMemo, useRef } from 'react';

import type { IBaseAttributes, Model } from '@feathr/rachis';

type TEphemeralMailingModelClass<TMailing, TModel> = new (attributes: TMailing) => TModel;

export interface IUseEphemeralMailingOptions {
  // Throttle (ms) for forwarding edits; omit to forward each edit as it happens.
  delay?: number;
  // Whether the caller's copy of the seed holds unsaved changes.
  isSeedDirty?: boolean;
}

// An editable copy of an embedded mailing (drip step, A/B variant).
export function useEphemeralMailing<TMailing, TModel extends Model<TMailing & IBaseAttributes>>(
  ModelClass: TEphemeralMailingModelClass<TMailing, TModel>,
  seed: TMailing,
  key: string,
  onChange: (patch: Partial<TMailing>) => void,
  options?: IUseEphemeralMailingOptions,
): TModel;
export function useEphemeralMailing<TMailing, TModel extends Model<TMailing & IBaseAttributes>>(
  ModelClass: TEphemeralMailingModelClass<TMailing, TModel>,
  seed: TMailing | undefined,
  key: string | undefined,
  onChange: (patch: Partial<TMailing>) => void,
  options?: IUseEphemeralMailingOptions,
): TModel | undefined;
export function useEphemeralMailing<TMailing, TModel extends Model<TMailing & IBaseAttributes>>(
  ModelClass: TEphemeralMailingModelClass<TMailing, TModel>,
  seed: TMailing | undefined,
  key: string | undefined,
  onChange: (patch: Partial<TMailing>) => void,
  { delay, isSeedDirty }: IUseEphemeralMailingOptions = {},
): TModel | undefined {
  const ephemeral = useMemo(() => (seed ? new ModelClass(toJS(seed)) : undefined), [key]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const forwardedRef = useRef<Partial<TMailing>>({});

  useEffect(() => {
    if (!ephemeral) {
      return undefined;
    }
    forwardedRef.current = {};
    const dispose = reaction(
      () => ephemeral.toDirtyJS() as Partial<TMailing>,
      (patch) => {
        if (Object.keys(patch).length > 0) {
          forwardedRef.current = patch;
          onChangeRef.current(patch);
        }
      },
      { delay },
    );
    return () => {
      dispose();
      // Flush only an edit still inside the throttle window, so a discarded edit is never re-sent.
      const pending = ephemeral.toDirtyJS() as Partial<TMailing>;
      if (Object.keys(pending).length > 0 && !isEqual(pending, forwardedRef.current)) {
        onChangeRef.current(pending);
      }
    };
  }, [ephemeral]);

  useEffect(() => {
    if (!ephemeral || !seed || isSeedDirty !== false) {
      return;
    }
    const changedAttributes = [...ephemeral.dirtyAttributes];
    const seedAttributes = toJS(seed);
    const seedKeys = Object.keys(seedAttributes);
    runInAction(() => {
      changedAttributes
        .filter((attribute) => !seedKeys.includes(attribute))
        .forEach((attribute) => ephemeral.unset(attribute, false));
      ephemeral.set(seedAttributes, false);
      ephemeral.setAttributeClean(...changedAttributes, ...seedKeys);
    });
    forwardedRef.current = {};
  }, [seed, isSeedDirty]);

  return ephemeral;
}
